/**
 * KAN-862: группа кнопок-переключателей (role group, выбранная aria-pressed).
 * Панель доски CRM и переключатель "Таблица | Доска".
 * KAN-906: compact - высота 32px (компактная строка фильтров таблицы CRM); у пункта могут быть
 * значок-счётчик (badge), подсказка (title) и свой data-testid; scrollable - группа не
 * переносится, на узком экране прокручивается горизонтально.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T> {
  value: T;
  label: string;
  /** KAN-906: значок после подписи (счётчик). */
  badge?: ReactNode;
  /** KAN-906: подсказка при наведении. */
  title?: string;
  /** KAN-906: data-testid кнопки пункта. */
  testId?: string;
}

interface SegmentedControlProps<T> {
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Подпись над группой видна (панель доски) или только для чтения с экрана. */
  showLabel?: boolean;
  testId?: string;
  className?: string;
  /** KAN-906: компактная высота (h-8). */
  compact?: boolean;
  /** KAN-906: без переноса, горизонтальная прокрутка на узком экране. */
  scrollable?: boolean;
}

export function SegmentedControl<T extends string | number | null>({
  label,
  options,
  value,
  onChange,
  showLabel = false,
  testId,
  className,
  compact = false,
  scrollable = false,
}: SegmentedControlProps<T>): JSX.Element {
  return (
    <div
      className={cn(
        'flex min-w-0 shrink-0 items-center gap-2',
        scrollable && 'max-w-full shrink overflow-x-auto',
        className
      )}
    >
      {showLabel && <span className="hidden text-xs font-medium text-gray-500 lg:inline">{label}</span>}
      <div
        role="group"
        aria-label={label}
        data-testid={testId}
        className={cn('inline-flex rounded-lg border bg-gray-50 p-0.5', scrollable && 'shrink-0')}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={String(option.value)}
              type="button"
              aria-pressed={active}
              title={option.title}
              data-testid={option.testId}
              onClick={() => onChange(option.value)}
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-sm font-medium transition-colors',
                compact ? 'py-[3px]' : 'py-1',
                active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
              )}
            >
              {option.label}
              {option.badge}
            </button>
          );
        })}
      </div>
    </div>
  );
}
